<template>
    <section class="form-section">
        <h2>Путь по сезонам</h2>
        <p class="section-description">Выберите первый и последний сезон, в которых вы играли/работали в
            чемпионате. Затем заполните информацию по каждому сезону.</p>
        <p class="section-hint">
            Снимите галочку у турнира, если не участвовали в нём за эту команду.
        </p>

        <div class="form-grid" style="margin-bottom: 2rem;">
            <div class="form-group">
                <label class="input-label" for="first_season_id">
                    Первый сезон<span v-if="isBlank(firstSeasonId)" class="required-mark" aria-hidden="true">*</span>
                </label>
                <UiSelect id="first_season_id" :model-value="firstSeasonId" label="" :options="seasonOptions"
                    required block value-type="number" placeholder="Выберите первый сезон"
                    :error="errors?.first_season_id" @update:model-value="onFirstSeasonChange" />
            </div>

            <div class="form-group">
                <label class="input-label" for="last_season_id">
                    Последний сезон<span v-if="isBlank(lastSeasonId)" class="required-mark" aria-hidden="true">*</span>
                </label>
                <UiSelect id="last_season_id" :model-value="lastSeasonId" label="" :options="seasonOptions"
                    required block value-type="number" placeholder="Выберите последний сезон"
                    :error="errors?.last_season_id" @update:model-value="onLastSeasonChange" />
            </div>
        </div>

        <div v-if="errors?.seasons" class="input-error" style="margin-bottom: 1rem;">
            <p>{{ errors.seasons }}</p>
        </div>
        <div v-if="photoValidationError || serverPhotoError" class="input-error" style="margin-bottom: 1rem;">
            <p>{{ photoValidationError || serverPhotoError }}</p>
        </div>

        <div v-if="seasons.length > 0" class="seasons-flex-wrapper">
            <!-- Заголовки -->
            <div class="seasons-header">
                <div class="season-col-header">Сезон</div>
                <div class="status-col-header">Статус</div>
                <div class="team-col-header">Команда</div>
                <div class="position-col-header">Позиция</div>
                <div class="number-col-header">Игровой номер</div>
                <div class="photo-col-header">Фото</div>
                <div class="actions-col-header" aria-hidden="true"></div>
            </div>

            <!-- Блоки сезонов (каждый сезон может содержать несколько записей) -->
            <div v-for="(seasonGroup, seasonIndex) in seasons" :key="seasonGroup.season_id" class="season-row">
                <!-- Сезон -->
                <div class="season-col">
                    <div class="season-name">{{ getSeasonShort(seasonGroup.season_id) }}</div>
                    <button type="button" @click="addEntry(seasonIndex)" title="Добавить команду"
                        aria-label="Добавить команду">
                        <img src="/images/icons/add.svg" alt="Добавить команду" />
                    </button>
                </div>

                <div class="season-entries-wrapper">
                    <div v-for="(entry, entryIndex) in seasonGroup.entries"
                        :key="`${seasonGroup.season_id}_${entryIndex}`" class="season-entry-block">
                        <div
                            class="season-entry-row"
                            :class="entry.status === 'played' ? 'is-played' : 'is-inactive'"
                        >
                        <div class="status-col">
                            <span class="season-field-label">Статус</span>
                            <UiSelect :id="`status_${seasonIndex}_${entryIndex}`" :model-value="entry.status"
                                label="" :options="statusOptions" required block value-type="string" class="status-select"
                                :error="errors?.[`seasons.${seasonIndex}.entries.${entryIndex}.status`]"
                                @update:model-value="onStatusChange(seasonIndex, entryIndex, $event)" />
                        </div>

                        <div class="team-col">
                            <span class="season-field-label">Команда</span>
                            <div v-if="entry.status === 'played'">
                                <div class="team-select-wrapper">
                                    <span
                                        v-if="isBlank(entry.team_id)"
                                        class="required-mark season-field-required"
                                        aria-hidden="true"
                                    >*</span>
                                    <UiSelect :id="`team_${seasonIndex}_${entryIndex}`" :model-value="entry.team_id"
                                        label="" :options="teamOptions(seasonGroup.season_id)" required block value-type="number"
                                        class="team-select" :error="errors?.[`seasons.${seasonIndex}.entries.${entryIndex}.team_id`]"
                                        placeholder="Выберите" @update:model-value="onTeamChange(seasonIndex, entryIndex, $event)" />
                                </div>
                            </div>
                            <div v-else class="status-badge">
                                <span v-if="entry.status === 'not_played'">—</span>
                                <span v-else>Другой</span>
                            </div>
                        </div>

                        <div class="position-col">
                            <span class="season-field-label">Позиция</span>
                            <div v-if="entry.status === 'played'" class="position-select-wrapper">
                                <span
                                    v-if="isBlank(entry.position_id)"
                                    class="required-mark season-field-required"
                                    aria-hidden="true"
                                >*</span>
                                <UiSelect :id="`position_${seasonIndex}_${entryIndex}`" :model-value="entry.position_id"
                                    label="" :options="positionOptions" required block value-type="number" class="position-select"
                                    placeholder="Выберите" :error="errors?.[`seasons.${seasonIndex}.entries.${entryIndex}.position_id`]"
                                    @update:model-value="onPositionChange(seasonIndex, entryIndex, $event)" />
                            </div>
                            <div v-else class="status-badge">—</div>
                        </div>

                        <div class="number-col">
                            <span class="season-field-label">Игровой номер</span>
                            <div v-if="entry.status === 'played'">
                                <UiInput :id="`number_${seasonIndex}_${entryIndex}`" type="number"
                                    :model-value="entry.number ? String(entry.number) : ''" label="" min="1" max="99"
                                    class="number-input" placeholder="—" :error="errors?.[`seasons.${seasonIndex}.entries.${entryIndex}.number`]"
                                    @update:model-value="onNumberChange(seasonIndex, entryIndex, $event)" />
                            </div>
                            <div v-else class="status-badge">—</div>
                        </div>

                        <div class="photo-col">
                            <span class="season-field-label">Фото</span>
                            <div v-if="entry.status === 'played'" class="photo-upload-cell">
                                <div v-if="hasSeasonPhoto(entry)"
                                    class="photo-cell-preview">
                                    <img :src="localPhotoPreviews[entry.client_key] || entry.photo_url"
                                        alt="Фото" class="photo-cell-img" />
                                </div>
                                <button v-if="hasSeasonPhoto(entry)" type="button"
                                    class="photo-remove-btn" title="Удалить фото" aria-label="Удалить фото"
                                    @click="onPhotoRemove(entryIndex, seasonIndex, $event)">
                                    <img src="/images/icons/delete.svg" alt="Удалить фото" width="16" height="16" />
                                </button>
                                <label v-else :for="`photo_${seasonIndex}_${entryIndex}`" class="photo-upload-btn"
                                    title="Загрузить фото">
                                    <img src="/images/icons/camera.svg" alt="Загрузить фото" width="16" height="16" />
                                </label>
                                <input :id="`photo_${seasonIndex}_${entryIndex}`" type="file"
                                    accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png" class="photo-file-input"
                                    @change="onPhotoChange(seasonGroup.season_id, entryIndex, $event)" />
                            </div>
                            <div v-else class="status-badge">—</div>
                        </div>

                        <div class="actions-col">
                            <button type="button" @click="removeEntry(seasonIndex, entryIndex)" title="Удалить"
                                aria-label="Удалить">
                                <img src="/images/icons/delete.svg" alt="Удалить" />
                            </button>
                        </div>
                        </div>

                        <div v-if="shouldShowTitleClaims(entry)"
                            class="season-entry-titles">
                            <span class="season-entry-titles-label">Места команды в турнирах</span>
                            <div class="season-entry-titles-list">
                                <label v-for="competition in COMPETITION_ORDER"
                                    :key="competition"
                                    class="season-entry-title-item"
                                    :class="{ 'is-disabled': !hasTeamPlace(getTeamPlace(seasonGroup.season_id, entry.team_id, competition)) }">
                                    <input type="checkbox"
                                        :checked="entry.title_claims?.[competition] !== false"
                                        :disabled="!hasTeamPlace(getTeamPlace(seasonGroup.season_id, entry.team_id, competition))"
                                        @change="onTitleClaimChange(seasonIndex, entryIndex, competition, $event)" />
                                    <span class="season-entry-title-label">
                                        <img
                                            :src="COMPETITION_ICONS[competition]"
                                            :alt="COMPETITION_LABELS[competition]"
                                            :title="COMPETITION_LABELS[competition]"
                                            class="season-entry-competition-icon"
                                        />
                                        <PlaceBadge
                                            v-if="isMedalPlace(getTeamPlace(seasonGroup.season_id, entry.team_id, competition))"
                                            :place="getTeamPlace(seasonGroup.season_id, entry.team_id, competition)"
                                        />
                                        <span v-else>
                                            {{ formatPlaceWithWord(getTeamPlace(seasonGroup.season_id, entry.team_id, competition)) }}
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import PlaceBadge from '@/components/player/PlaceBadge.vue';
import { isBlank } from '@/utils/formHelpers';
import { formatSeasonShort } from '@/utils/formatSeason';
import { COMPETITION_ICONS } from '@/utils/skillQualityConfig';
import UiInput from '@/components/ui/forms/UiInput.vue';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const COMPETITION_ORDER = ['supercup', 'cup', 'championship'];

const COMPETITION_LABELS = {
    supercup: 'Суперкубок',
    cup: 'Кубок',
    championship: 'Чемпионат',
};

const seasonOptions = computed(() => props.seasonsList.map((season) => ({ value: season.id, label: season.name })));
const positionOptions = computed(() => [...props.positions]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((position) => ({ value: position.id, label: position.name })));
const statusOptions = [
    { value: 'played', label: 'Участвовал' },
    { value: 'not_played', label: 'Не участвовал' },
    { value: 'other_championship', label: 'Другой чемпионат' },
];

const props = defineProps({
    firstSeasonId: [Number, String],
    lastSeasonId: [Number, String],
    seasons: {
        type: Array,
        default: () => []
    },
    seasonsList: {
        type: Array,
        required: true
    },
    teams: {
        type: Array,
        required: true
    },
    teamsBySeason: {
        type: Object,
        default: () => ({})
    },
    positions: {
        type: Array,
        required: true
    },
    existingSeasons: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:firstSeasonId', 'update:lastSeasonId', 'update:seasons', 'photoChange', 'photoClear']);

const hasTeamPlace = (place) => place !== null && place !== undefined && place !== '';

const getTeamPlaces = (seasonId, teamId) => {
    if (!seasonId || !teamId) {
        return {};
    }

    const team = getTeamsForSeason(seasonId).find(t => t.id === teamId);

    return team?.places ?? {};
};

const getTeamPlace = (seasonId, teamId, competition) => {
    const places = getTeamPlaces(seasonId, teamId);

    return places[competition] ?? null;
};

const MEDAL_PLACES = new Set(['1', '2', '3']);

const isMedalPlace = (place) => hasTeamPlace(place) && MEDAL_PLACES.has(String(place).trim());

const formatPlaceWithWord = (place) => {
    if (!hasTeamPlace(place)) {
        return '—';
    }

    const raw = String(place).trim();
    const rangeMatch = raw.match(/^(\d+)\s*[-–—]\s*(\d+)$/);

    if (rangeMatch) {
        return `${rangeMatch[1]}-${rangeMatch[2]} место`;
    }

    return `${raw} место`;
};

const makeClientKey = (seasonId) => {
    const rand = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    return `temp_${seasonId}_${rand}`;
};

const clientKeyFor = (seasonId, entry = {}) => {
    if (entry.client_key) {
        return entry.client_key;
    }

    if (entry.roster_id) {
        return String(entry.roster_id);
    }

    return makeClientKey(seasonId);
};

const defaultTitleClaims = (seasonId, teamId, existingClaims = {}) => {
    const claims = {};

    for (const competition of COMPETITION_ORDER) {
        claims[competition] = existingClaims[competition] !== false;
    }

    return claims;
};

const shouldShowTitleClaims = (entry) =>
    entry.status === 'played' && Boolean(entry.team_id);

const defaultEntry = (status = 'not_played', seasonId = 'new') => ({
    status,
    team_id: null,
    position_id: null,
    number: null,
    title_claims: {},
    roster_id: null,
    client_key: makeClientKey(seasonId),
    photo_url: null,
});

const MAX_PHOTO_BYTES = 32 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const isAllowedPhotoFile = (file) => {
    if (ALLOWED_PHOTO_TYPES.includes(file.type)) {
        return true;
    }

    const extension = file.name?.split('.').pop()?.toLowerCase();

    return extension ? ALLOWED_PHOTO_EXTENSIONS.includes(extension) : false;
};

// Локальные превью для выбранных файлов (ещё не загруженных)
const localPhotoPreviews = ref({});
const photoValidationError = ref(null);

const serverPhotoError = computed(() => {
    if (!props.errors) {
        return null;
    }

    for (const [key, message] of Object.entries(props.errors)) {
        if (key.startsWith('season_photos.')) {
            return message;
        }
    }

    return null;
});

const hasSeasonPhoto = (entry) =>
    Boolean(localPhotoPreviews.value[entry?.client_key] || entry?.photo_url);

const clearLocalPhotoPreview = (entry, hadServerPhoto = false) => {
    const clientKey = entry?.client_key;
    if (clientKey && localPhotoPreviews.value[clientKey]) {
        const { [clientKey]: _, ...rest } = localPhotoPreviews.value;
        localPhotoPreviews.value = rest;
    }

    emit('photoClear', { clientKey, hadServerPhoto });
};

const onPhotoRemove = (entryIndex, seasonIndex, event) => {
    const entry = props.seasons[seasonIndex]?.entries?.[entryIndex];
    const hadServerPhoto = Boolean(entry?.photo_url);
    const clientKey = entry?.client_key;

    const cell = event?.currentTarget?.closest?.('.photo-upload-cell');
    const input = cell?.querySelector('.photo-file-input');
    if (input) {
        input.value = '';
    }

    photoValidationError.value = null;

    if (clientKey && localPhotoPreviews.value[clientKey]) {
        const { [clientKey]: _, ...rest } = localPhotoPreviews.value;
        localPhotoPreviews.value = rest;
    }

    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) {
            return group;
        }

        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) {
                return en;
            }

            return { ...en, photo_url: null };
        });

        return { ...group, entries };
    });
    emit('update:seasons', updated);
    emit('photoClear', { clientKey, hadServerPhoto });
};

const getSeasonName = (seasonId) => {
    const season = props.seasonsList.find(s => s.id === seasonId);
    return season ? season.name : '';
};

const getSeasonShort = (seasonId) => {
    return formatSeasonShort(getSeasonName(seasonId) || String(seasonId));
};

const getTeamsForSeason = (seasonId) => {
    if (seasonId && props.teamsBySeason && props.teamsBySeason[seasonId]) {
        return props.teamsBySeason[seasonId];
    }
    // Фоллбэк на полный список команд, если маппинг не найден
    return props.teams;
};

const teamOptions = (seasonId) => [...getTeamsForSeason(seasonId)]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((team) => ({ value: team.id, label: team.name }));

const onPhotoChange = (seasonId, entryIndex, event) => {
    const file = event.target.files?.[0];
    photoValidationError.value = null;

    if (!file) {
        return;
    }

    if (!isAllowedPhotoFile(file)) {
        photoValidationError.value = 'Допустимы только изображения JPG и PNG.';
        event.target.value = '';
        return;
    }

    if (file.size > MAX_PHOTO_BYTES) {
        photoValidationError.value = 'Размер файла не должен превышать 32 МБ.';
        event.target.value = '';
        return;
    }

    const group = props.seasons.find(g => g.season_id === seasonId);
    const entry = group?.entries?.[entryIndex];
    const clientKey = clientKeyFor(seasonId, entry);

    const reader = new FileReader();
    reader.onload = (e) => {
        const previewUrl = e.target?.result ?? null;
        localPhotoPreviews.value = {
            ...localPhotoPreviews.value,
            [clientKey]: previewUrl,
        };
        emit('photoChange', { clientKey, file, previewUrl });
    };
    reader.onerror = () => {
        photoValidationError.value = 'Не удалось прочитать файл. Попробуйте другой JPG или PNG.';
        event.target.value = '';
    };
    reader.readAsDataURL(file);
};

const updateSeasonsList = () => {
    if (!props.firstSeasonId || !props.lastSeasonId) {
        emit('update:seasons', []);
        return;
    }

    const firstSeason = props.seasonsList.find(s => s.id === props.firstSeasonId);
    const lastSeason = props.seasonsList.find(s => s.id === props.lastSeasonId);

    if (!firstSeason || !lastSeason) {
        emit('update:seasons', []);
        return;
    }

    // Получаем все сезоны между первым и последним
    const seasonsBetween = props.seasonsList.filter(s =>
        s.id >= firstSeason.id && s.id <= lastSeason.id
    ).sort((a, b) => b.id - a.id);

    // Map текущих (введённых пользователем) групп по season_id, чтобы сохранить их при изменении границ
    const existingMap = {};
    (props.seasons || []).forEach(g => {
        if (g && g.season_id !== undefined) existingMap[g.season_id] = g;
    });

    // Map серверных existingSeasons (если они есть)
    const serverMap = {};
    (props.existingSeasons || []).forEach(g => {
        if (g && g.season_id !== undefined) serverMap[g.season_id] = g;
    });

    const newSeasons = seasonsBetween.map(season => {
        const seasonId = season.id;
        // Сохраняем пользовательские данные, если они есть
        if (existingMap[seasonId]) {
            const group = existingMap[seasonId];

            return {
                ...group,
                entries: (group.entries || []).map((en) => ({
                    ...en,
                    client_key: clientKeyFor(seasonId, en),
                })),
            };
        }
        // Иначе используем серверные данные если есть
        if (serverMap[seasonId] && Array.isArray(serverMap[seasonId].entries) && serverMap[seasonId].entries.length > 0) {
            const entries = serverMap[seasonId].entries.map(en => ({
                status: en.status || 'played',
                team_id: en.team_id || null,
                position_id: en.position_id || null,
                number: en.number ? Number(en.number) : null,
                title_claims: en.team_id
                    ? defaultTitleClaims(seasonId, en.team_id, en.title_claims ?? {})
                    : {},
                roster_id: en.roster_id || null,
                client_key: clientKeyFor(seasonId, en),
                photo_url: en.photo_url || null,
            }));
            return { season_id: seasonId, entries };
        }
        // Фоллбэк — одна пустая запись
        return { season_id: seasonId, entries: [defaultEntry('not_played', seasonId)] };
    });

    emit('update:seasons', newSeasons);
};

const onFirstSeasonChange = (value) => {
    emit('update:firstSeasonId', value ? Number(value) : null);
};

const onLastSeasonChange = (value) => {
    emit('update:lastSeasonId', value ? Number(value) : null);
};

const onStatusChange = (seasonIndex, entryIndex, newStatus) => {
    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) return en;
            return {
                ...en,
                status: newStatus,
                team_id: newStatus === 'played' ? en.team_id : null,
                position_id: newStatus === 'played' ? en.position_id : null,
                number: newStatus === 'played' ? en.number : null,
                title_claims: newStatus === 'played' ? en.title_claims : {},
            };
        });
        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const onTeamChange = (seasonIndex, entryIndex, value) => {
    const teamId = value ? Number(value) : null;
    const seasonId = props.seasons[seasonIndex]?.season_id;

    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) return en;
            return {
                ...en,
                team_id: teamId,
                title_claims: teamId ? defaultTitleClaims(seasonId, teamId) : {},
            };
        });
        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const onPositionChange = (seasonIndex, entryIndex, value) => {
    const positionId = value ? Number(value) : null;

    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;

        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) return en;

            return {
                ...en,
                position_id: positionId,
                title_claims: defaultTitleClaims(group.season_id, en.team_id, en.title_claims),
            };
        });

        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const onTitleClaimChange = (seasonIndex, entryIndex, competition, event) => {
    if (event.target.disabled) {
        return;
    }

    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) return en;
            return {
                ...en,
                title_claims: {
                    ...(en.title_claims ?? {}),
                    [competition]: event.target.checked,
                },
            };
        });
        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const onNumberChange = (seasonIndex, entryIndex, value) => {
    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = group.entries.map((en, eIdx) => {
            if (eIdx !== entryIndex) return en;
            return { ...en, number: value ? Number(value) : null };
        });
        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const addEntry = (seasonIndex) => {
    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = [...group.entries, defaultEntry('played', group.season_id)];
        return { ...group, entries };
    });
    emit('update:seasons', updated);
};

const removeEntry = (seasonIndex, entryIndex) => {
    const entry = props.seasons[seasonIndex]?.entries?.[entryIndex];
    if (entry) {
        clearLocalPhotoPreview(entry, Boolean(entry.photo_url));
    }

    const updated = props.seasons.map((group, sIdx) => {
        if (sIdx !== seasonIndex) return group;
        const entries = group.entries.filter((_, eIdx) => eIdx !== entryIndex);
        const normalizedEntries = entries.length ? entries : [defaultEntry('not_played', group.season_id)];
        return { ...group, entries: normalizedEntries };
    });
    emit('update:seasons', updated);
};

// Инициализация при монтировании, если данные уже есть
onMounted(() => {
    if (props.firstSeasonId && props.lastSeasonId && (!props.seasons || props.seasons.length === 0)) {
        updateSeasonsList();
    }
});

// Watcher для обновления списка сезонов при изменении firstSeasonId или lastSeasonId
watch([() => props.firstSeasonId, () => props.lastSeasonId], ([newFirst, newLast], [oldFirst, oldLast]) => {
    if ((newFirst !== oldFirst || newLast !== oldLast) && newFirst && newLast) {
        updateSeasonsList();
    }
});
</script>

<style lang="scss" scoped>
.section-hint {
    margin: 0 0 1rem;
    padding: 0.75rem 1rem;
    background: rgba(255, 193, 7, 0.12);
    border-radius: 6px;
    font-size: 0.9rem;
    color: #856404;
}

.season-entry-title-label :deep(.places-medal) {
    width: 22px;
    height: 22px;
    display: inline-block;
    vertical-align: middle;
}
</style>
